import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { FilterOptions, type FilterOption } from './FilterOptions';

type Props = {
  label: string;
  icon?: LucideIcon;
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  multiple?: boolean;
  /** People get initials avatars in the list. */
  people?: boolean;
};

/** A filter button that opens a searchable list under it. A single-choice menu closes on pick. */
export function FilterMenu({ label, icon: Icon, options, selected, onChange, multiple = false, people = false }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isOpen = anchor !== null;
  const shown = selected.length === 0 ? label : multiple ? `${label} · ${selected.length}` : (selected[0] ?? label);

  const toggle = (value: string) => {
    if (!multiple) {
      onChange(selected.includes(value) ? [] : [value]);
      setAnchor(null);
      return;
    }
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  return (
    <>
      <ButtonBase aria-haspopup="listbox" aria-expanded={isOpen} aria-label={`${label} filter`} onClick={(e) => { setAnchor(e.currentTarget); }} sx={triggerSx(isOpen || selected.length > 0)}>
        {Icon ? <Icon size={14} aria-hidden /> : null}
        <Box component="span" sx={{ flexGrow: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shown}</Box>
        <Box component="span" sx={{ display: 'flex', transition: 'transform 160ms', transform: isOpen ? 'rotate(180deg)' : 'none' }}>
          <ChevronDown size={14} aria-hidden />
        </Box>
      </ButtonBase>
      <Popover
        open={isOpen}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.75, borderRadius: 1.5 } } }}
      >
        <FilterOptions
          options={options}
          selected={selected}
          multiple={multiple}
          people={people}
          searchLabel={`Find an ${label.toLowerCase()}`}
          onToggle={toggle}
          onClear={() => { onChange([]); }}
        />
      </Popover>
    </>
  );
}

const triggerSx = (isActive: boolean) => ({
  gap: 1,
  width: 168,
  height: 32,
  px: 1.25,
  borderRadius: 1.5,
  fontSize: '0.8125rem',
  fontWeight: 500,
  border: 1,
  borderColor: isActive ? 'primary.light' : 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  transition: 'border-color 150ms, box-shadow 150ms',
  boxShadow: isActive ? '0 0 0 3px color-mix(in srgb, var(--mui-palette-primary-main) 10%, transparent)' : 'none',
  '& svg': { color: 'text.secondary', flexShrink: 0 },
  '&:hover': { borderColor: isActive ? 'primary.light' : 'surface.border' },
});
